import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Hash, Loader2, Plus, Settings, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { tagsApi, tagsQueryKeys } from '../lib/tags-api';
import type { Tag, TagsFieldProps } from '../types/tags';

/**
 * Enhanced TagsField component with search, create, and chip functionality
 *
 * Features:
 * - Command-style dropdown with suggestions and search
 * - Optimistic UI with rollback on errors
 * - Keyboard navigation (Enter, Tab, Backspace, Esc, ↑/↓)
 * - Paste support for comma/semicolon/newline separated values
 * - Case-insensitive deduplication and normalization
 * - Stable color generation from tag names
 * - Quick-add row with recent and popular tags
 */
export function TagsField({
	value,
	onChange,
	maxTags = 10,
	disabled = false,
	placeholder = "Start typing to search or create a tag. Try: 'customer', 'marketing', 'priority-high'.",
	className = '',
	workflowId,
}: TagsFieldProps) {
	const [inputValue, setInputValue] = useState('');
	const [showDropdown, setShowDropdown] = useState(false);
	const [selectedIndex, setSelectedIndex] = useState(-1);
	const [optimisticTags, setOptimisticTags] = useState<Tag[]>([]);
	const inputRef = useRef<HTMLInputElement>(null);
	const queryClient = useQueryClient();

	// Debounced search query
	const [debouncedQuery, setDebouncedQuery] = useState('');
	useEffect(() => {
		const timer = setTimeout(() => setDebouncedQuery(inputValue.trim()), 200);
		return () => clearTimeout(timer);
	}, [inputValue]);

	// Search tags with fuzzy matching
	const { data: searchResults = [], isLoading: isSearching } = useQuery({
		queryKey: tagsQueryKeys.search(debouncedQuery),
		queryFn: () => tagsApi.searchTags({ q: debouncedQuery, limit: 20 }),
		enabled: debouncedQuery.length > 0,
		staleTime: 5 * 60 * 1000, // 5 minutes
	});

	// Get tag suggestions (most used)
	const { data: suggestions = [] } = useQuery({
		queryKey: tagsQueryKeys.suggest(10),
		queryFn: () => tagsApi.suggestTags({ limit: 10 }),
		staleTime: 10 * 60 * 1000, // 10 minutes
	});

	// Create tag mutation
	const createTagMutation = useMutation({
		mutationFn: tagsApi.createTag,
		onSuccess: newTag => {
			// Update cache
			queryClient.invalidateQueries({ queryKey: tagsQueryKeys.all });
			// Add to selected tags
			onChange([...value, newTag]);
			// Clear optimistic state
			setOptimisticTags([]);
		},
		onError: () => {
			// Remove optimistic tag on error
			setOptimisticTags([]);
		},
	});

	// Attach tag to workflow mutation (if workflowId provided)
	const attachTagMutation = useMutation({
		mutationFn: ({ tagId }: { tagId: string }) =>
			tagsApi.attachTagToWorkflow(workflowId!, tagId),
		onSuccess: () => {
			if (workflowId) {
				queryClient.invalidateQueries({ queryKey: tagsQueryKeys.workflow(workflowId) });
			}
		},
	});

	// Detach tag from workflow mutation (if workflowId provided)
	const detachTagMutation = useMutation({
		mutationFn: ({ tagId }: { tagId: string }) =>
			tagsApi.detachTagFromWorkflow(workflowId!, tagId),
		onSuccess: () => {
			if (workflowId) {
				queryClient.invalidateQueries({ queryKey: tagsQueryKeys.workflow(workflowId) });
			}
		},
	});

	// Normalize tag name
	const normalizeTagName = (name: string): string => {
		return name.trim().replace(/\s+/g, ' ');
	};

	// Check if tag already exists (case-insensitive)
	const isTagSelected = (tagName: string): boolean => {
		const normalized = normalizeTagName(tagName).toLowerCase();
		return [...value, ...optimisticTags].some(tag => tag.name.toLowerCase() === normalized);
	};

	// Generate stable color hue for tag
	const getTagColorHue = (name: string): number => {
		return Array.from(name).reduce((acc, char) => (acc * 31 + char.charCodeAt(0)) % 360, 0);
	};

	// Get HSL color from hue
	const getTagColor = (hue: number) => ({
		backgroundColor: `hsl(${hue}, 45%, 90%)`,
		color: `hsl(${hue}, 70%, 35%)`,
		borderColor: `hsl(${hue}, 45%, 80%)`,
	});

	// Get dark mode HSL color from hue
	const getTagColorDark = (hue: number) => ({
		backgroundColor: `hsl(${hue}, 30%, 20%)`,
		color: `hsl(${hue}, 60%, 80%)`,
		borderColor: `hsl(${hue}, 30%, 30%)`,
	});

	// Filter available options
	const availableOptions = debouncedQuery
		? searchResults.filter(tag => !isTagSelected(tag.name))
		: suggestions.filter(tag => !isTagSelected(tag.name)).slice(0, 5);

	// Check if we should show "Create" option
	const normalizedInput = normalizeTagName(inputValue);
	const showCreateOption =
		normalizedInput.length >= 2 &&
		normalizedInput.length <= 40 &&
		!isTagSelected(normalizedInput) &&
		!/[,;]/.test(normalizedInput) && // No forbidden characters
		!availableOptions.some(tag => tag.name.toLowerCase() === normalizedInput.toLowerCase());

	// All dropdown options
	const allOptions = [
		...availableOptions,
		...(showCreateOption
			? [
					{
						id: 'create',
						name: normalizedInput,
						slug: '',
						orgId: '',
						usageCount: 0,
						createdAt: '',
						updatedAt: '',
						colorHue: getTagColorHue(normalizedInput),
						isCreateOption: true,
					} as Tag & { isCreateOption: boolean },
			  ]
			: []),
	];

	// Add tag
	const addTag = useCallback(
		(tag: Tag | { name: string }) => {
			if (value.length >= maxTags) {
				return; // Respect max tags limit
			}

			const tagName = normalizeTagName(tag.name);
			if (!tagName || isTagSelected(tagName)) {
				return;
			}

			if ('id' in tag && tag.id !== 'create') {
				// Existing tag
				onChange([...value, tag]);

				// Attach to workflow if provided
				if (workflowId) {
					attachTagMutation.mutate({ tagId: tag.id });
				}
			} else {
				// Create new tag
				const optimisticTag: Tag = {
					id: `temp-${Date.now()}`,
					orgId: '',
					name: tagName,
					slug: tagName.toLowerCase().replace(/\s+/g, '-'),
					usageCount: 0,
					createdAt: new Date().toISOString(),
					updatedAt: new Date().toISOString(),
					colorHue: getTagColorHue(tagName),
				};

				// Add optimistic tag
				setOptimisticTags([optimisticTag]);

				// Create tag on server
				createTagMutation.mutate({ name: tagName });
			}

			setInputValue('');
			setShowDropdown(false);
			setSelectedIndex(-1);
		},
		[value, onChange, maxTags, workflowId, attachTagMutation, createTagMutation]
	);

	// Remove tag
	const removeTag = useCallback(
		(tagToRemove: Tag) => {
			onChange(value.filter(tag => tag.id !== tagToRemove.id));

			// Detach from workflow if provided
			if (workflowId && tagToRemove.id !== `temp-${Date.now()}`) {
				detachTagMutation.mutate({ tagId: tagToRemove.id });
			}
		},
		[value, onChange, workflowId, detachTagMutation]
	);

	// Handle paste
	const handlePaste = useCallback(
		(e: React.ClipboardEvent) => {
			e.preventDefault();
			const pastedText = e.clipboardData.getData('text');
			const tagNames = pastedText
				.split(/[,;\n]/)
				.map(name => normalizeTagName(name))
				.filter(name => name.length >= 2 && name.length <= 40 && !/[,;]/.test(name))
				.slice(0, maxTags - value.length); // Respect max tags limit

			tagNames.forEach(name => {
				if (!isTagSelected(name)) {
					addTag({ name });
				}
			});
		},
		[addTag, maxTags, value.length]
	);

	// Handle keyboard navigation
	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent) => {
			if (e.key === 'ArrowDown') {
				e.preventDefault();
				setSelectedIndex(prev => Math.min(prev + 1, allOptions.length - 1));
				setShowDropdown(true);
			} else if (e.key === 'ArrowUp') {
				e.preventDefault();
				setSelectedIndex(prev => Math.max(prev - 1, -1));
			} else if (e.key === 'Enter') {
				e.preventDefault();
				if (selectedIndex >= 0 && allOptions[selectedIndex]) {
					addTag(allOptions[selectedIndex]);
				} else if (normalizedInput) {
					addTag({ name: normalizedInput });
				}
			} else if (e.key === 'Tab' || e.key === ',') {
				e.preventDefault();
				if (normalizedInput) {
					addTag({ name: normalizedInput });
				}
			} else if (e.key === 'Escape') {
				setShowDropdown(false);
				setSelectedIndex(-1);
			} else if (e.key === 'Backspace' && !inputValue && value.length > 0) {
				// Remove last tag if input is empty
				removeTag(value[value.length - 1]);
			}
		},
		[selectedIndex, allOptions, normalizedInput, addTag, inputValue, value, removeTag]
	);

	// Handle input change
	const handleInputChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const newValue = e.target.value;
			setInputValue(newValue);
			setShowDropdown(newValue.length > 0 || suggestions.length > 0);
			setSelectedIndex(-1);
		},
		[suggestions.length]
	);

	// Handle input focus
	const handleInputFocus = useCallback(() => {
		setShowDropdown(true);
	}, []);

	// Handle input blur
	const handleInputBlur = useCallback(() => {
		// Delay to allow clicking on dropdown options
		setTimeout(() => {
			setShowDropdown(false);
			setSelectedIndex(-1);
		}, 150);
	}, []);

	// All selected tags (including optimistic)
	const allSelectedTags = [...value, ...optimisticTags];

	// Quick add suggestions (exclude already selected)
	const quickAddTags = suggestions.filter(tag => !isTagSelected(tag.name)).slice(0, 5);

	return (
		<div className={`relative ${className}`}>
			{/* Main input container */}
			<div className="flex flex-wrap gap-2 p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus-within:ring-2 focus-within:ring-blue-500 dark:focus-within:ring-blue-400 focus-within:border-blue-500 dark:focus-within:border-blue-400 min-h-[48px] bg-white dark:bg-gray-800 transition-colors">
				{/* Render selected tags as chips */}
				{allSelectedTags.map((tag, index) => {
					const colorHue = tag.colorHue || getTagColorHue(tag.name);
					const lightColors = getTagColor(colorHue);
					const darkColors = getTagColorDark(colorHue);

					return (
						<div
							key={tag.id}
							className="inline-flex items-center gap-1.5 px-2.5 py-1 text-sm rounded-md border transition-colors"
							style={
								{
									...lightColors,
									'--dark-bg': darkColors.backgroundColor,
									'--dark-color': darkColors.color,
									'--dark-border': darkColors.borderColor,
								} as React.CSSProperties
							}
						>
							<Hash className="w-3 h-3" />
							<span className="font-medium">{tag.name}</span>
							{tag.usageCount > 0 && (
								<span className="text-xs opacity-75">({tag.usageCount})</span>
							)}
							{!disabled && (
								<button
									type="button"
									onClick={() => removeTag(tag)}
									className="ml-1 text-current hover:opacity-75 focus:outline-none transition-opacity"
									aria-label={`Remove ${tag.name} tag`}
								>
									<X className="w-3 h-3" />
								</button>
							)}
							{createTagMutation.isPending && tag.id.startsWith('temp-') && (
								<Loader2 className="w-3 h-3 animate-spin ml-1" />
							)}
						</div>
					);
				})}

				{/* Tag count indicator */}
				{allSelectedTags.length > 0 && (
					<span className="text-xs text-gray-500 dark:text-gray-400 self-center">
						{allSelectedTags.length}/{maxTags} tags
					</span>
				)}

				{/* Input field */}
				<input
					ref={inputRef}
					type="text"
					value={inputValue}
					onChange={handleInputChange}
					onKeyDown={handleKeyDown}
					onFocus={handleInputFocus}
					onBlur={handleInputBlur}
					onPaste={handlePaste}
					disabled={disabled || allSelectedTags.length >= maxTags}
					placeholder={allSelectedTags.length === 0 ? placeholder : ''}
					className="flex-1 min-w-[200px] border-none outline-none bg-transparent text-sm text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 disabled:opacity-50"
				/>
			</div>

			{/* Dropdown */}
			{showDropdown && !disabled && (
				<div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-64 overflow-y-auto">
					{/* Loading state */}
					{isSearching && (
						<div className="flex items-center gap-2 px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
							<Loader2 className="w-4 h-4 animate-spin" />
							Searching...
						</div>
					)}

					{/* No results */}
					{!isSearching && debouncedQuery && allOptions.length === 0 && (
						<div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
							No tags found for "{debouncedQuery}"
						</div>
					)}

					{/* Options */}
					{allOptions.map((option, index) => {
						const isSelected = index === selectedIndex;
						const isCreate = 'isCreateOption' in option && option.isCreateOption;

						return (
							<button
								key={option.id}
								type="button"
								onClick={() => addTag(option)}
								className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 focus:bg-gray-100 dark:focus:bg-gray-700 focus:outline-none flex items-center gap-2 text-gray-900 dark:text-white transition-colors ${
									isSelected ? 'bg-gray-100 dark:bg-gray-700' : ''
								}`}
							>
								{isCreate ? (
									<>
										<Plus className="w-3 h-3 text-green-500" />
										<span>Create "{option.name}"</span>
									</>
								) : (
									<>
										<Hash className="w-3 h-3 text-gray-400 dark:text-gray-500" />
										<span>{option.name}</span>
										{option.usageCount > 0 && (
											<span className="text-xs text-gray-500 dark:text-gray-400 ml-auto">
												used in {option.usageCount}
											</span>
										)}
									</>
								)}
							</button>
						);
					})}

					{/* Quick add row */}
					{!debouncedQuery && quickAddTags.length > 0 && (
						<div className="border-t border-gray-200 dark:border-gray-600 p-2">
							<div className="text-xs text-gray-500 dark:text-gray-400 mb-2">
								Popular tags:
							</div>
							<div className="flex flex-wrap gap-1">
								{quickAddTags.map(tag => (
									<button
										key={tag.id}
										type="button"
										onClick={() => addTag(tag)}
										className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded transition-colors"
									>
										<Hash className="w-2.5 h-2.5" />
										{tag.name}
									</button>
								))}
							</div>
						</div>
					)}
				</div>
			)}

			{/* Help text */}
			<div className="mt-2 space-y-1">
				<p className="text-xs text-gray-500 dark:text-gray-400">
					Type and press Enter or comma to add tags. Paste comma-separated values for bulk add.
					{allSelectedTags.length >= maxTags && (
						<span className="text-orange-500 dark:text-orange-400 ml-1">
							Maximum {maxTags} tags reached.
						</span>
					)}
				</p>
				
				<Link
					to="/tags"
					className="inline-flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
				>
					<Settings className="w-3 h-3" />
					Manage tags
				</Link>
			</div>

			{/* Error states */}
			{createTagMutation.isError && (
				<p className="mt-1 text-xs text-red-500 dark:text-red-400">
					Failed to create tag. Please try again.
				</p>
			)}
		</div>
	);
}
